/**
 * VskiCredentialForm.tsx — the platform credential form (login / paste key).
 *
 * Extracted from VskiSetupPanel so the Connectivity wizard's login step and
 * the Settings → Storage tab share one implementation (two facades, one code
 * path). Owns the email/password + manual-key fields, the busy/feedback
 * states, and the mutations: `/api/vski/connect` logs in once and stores the
 * MINTED admin key (the password is never persisted); `/api/vski/key`
 * validates a pasted key server-side first. Responses never carry key
 * material — the caller reloads its status on `onChanged`.
 */
import { useState } from 'react';

import { connectVskiPlatform, saveVskiKey } from '../vski/vskiConfig';

interface VskiCredentialFormProps {
  /** HTTP origin of the backend (the mutation base). */
  backendOrigin: string | null;
  /** The backend API key (bearer for the routes). */
  token: string | null;
  /** Platform URL to scope the mutation (the caller's resolved probe/custom
   *  URL); omitted lets the backend decide (configured/discovered address). */
  platformUrl?: string;
  /** Whether the backend keystore is enabled (drives the env-var warning). */
  keystore: boolean;
  /** Fired after a successful store so the caller reloads its status. */
  onChanged: () => void;
}

type Feedback = { kind: 'ok'; text: string } | { kind: 'fail'; text: string } | null;

export function VskiCredentialForm({
  backendOrigin,
  token,
  platformUrl,
  keystore,
  onChanged,
}: VskiCredentialFormProps): JSX.Element {
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [manualKey, setManualKey] = useState('');
  const [mode, setMode] = useState<'login' | 'key'>('login');
  const [busy, setBusy] = useState<'connect' | 'key' | null>(null);
  const [feedback, setFeedback] = useState<Feedback>(null);

  const runConnect = (): void => {
    if (!backendOrigin) return;
    if (!email.trim() || !password) {
      setFeedback({ kind: 'fail', text: 'Email and password are required.' });
      return;
    }
    setBusy('connect');
    setFeedback(null);
    connectVskiPlatform(backendOrigin, token, {
      email: email.trim(),
      password,
      url: platformUrl,
    })
      .then((r) => {
        if (r.ok) {
          setFeedback({ kind: 'ok', text: '✓ Admin key minted and stored — storage is on the platform.' });
          setPassword('');
          onChanged();
        } else {
          setFeedback({ kind: 'fail', text: `✗ ${r.detail ?? 'platform login failed'}` });
        }
      })
      .catch((e: Error) => setFeedback({ kind: 'fail', text: `✗ ${e.message}` }))
      .finally(() => setBusy(null));
  };

  const runSaveKey = (): void => {
    if (!backendOrigin) return;
    if (!manualKey.trim()) {
      setFeedback({ kind: 'fail', text: 'Paste the platform API key first.' });
      return;
    }
    setBusy('key');
    setFeedback(null);
    saveVskiKey(backendOrigin, token, { apiKey: manualKey.trim(), url: platformUrl })
      .then((r) => {
        if (r.ok) {
          setFeedback({ kind: 'ok', text: '✓ Key stored — storage is on the platform.' });
          setManualKey('');
          onChanged();
        } else {
          setFeedback({ kind: 'fail', text: `✗ ${r.detail ?? 'key rejected'}` });
        }
      })
      .catch((e: Error) => setFeedback({ kind: 'fail', text: `✗ ${e.message}` }))
      .finally(() => setBusy(null));
  };

  return (
    <>
      <div className="vski-setup-modes">
        <button
          type="button"
          className={`btn btn-sm ${mode === 'login' ? 'btn-start' : ''}`}
          onClick={() => setMode('login')}
        >
          Login
        </button>
        <button
          type="button"
          className={`btn btn-sm ${mode === 'key' ? 'btn-start' : ''}`}
          onClick={() => setMode('key')}
        >
          Paste key
        </button>
      </div>
      {mode === 'login' ? (
        <>
          <label className="field">
            <span className="field-label">Platform admin email</span>
            <input
              type="text"
              className="text-input"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="admin@example.com"
              autoComplete="off"
              spellCheck={false}
            />
          </label>
          <label className="field">
            <span className="field-label">Password</span>
            <input
              type="password"
              className="text-input"
              value={password}
              onChange={(e) => setPassword(e.target.value)}
              placeholder="used once to mint the key — never stored"
            />
          </label>
          <div className="api-connect-actions">
            <button
              type="button"
              className="btn btn-start"
              onClick={runConnect}
              disabled={busy !== null}
            >
              {busy === 'connect' ? 'Connecting…' : 'Connect & store key'}
            </button>
          </div>
        </>
      ) : (
        <>
          <label className="field">
            <span className="field-label">Platform API key (admin role)</span>
            <input
              type="text"
              className="text-input"
              value={manualKey}
              onChange={(e) => setManualKey(e.target.value)}
              placeholder="paste a key created in the platform dashboard"
              spellCheck={false}
            />
          </label>
          <div className="api-connect-actions">
            <button
              type="button"
              className="btn btn-start"
              onClick={runSaveKey}
              disabled={busy !== null}
            >
              {busy === 'key' ? 'Saving…' : 'Validate & store key'}
            </button>
          </div>
        </>
      )}
      {!keystore && (
        <p className="vski-setup-warn">
          The backend's keystore is disabled — keys can only come from env
          vars until it is enabled.
        </p>
      )}
      {feedback && (
        <div className={`api-connect-test test-${feedback.kind}`}>{feedback.text}</div>
      )}
    </>
  );
}
